const DRM_VENDORS = {
  WIDEVINE: 'com.widevine.alpha',
  FAIRPLAY: 'com.apple.fps.1_0',
  PLAYREADY: 'com.microsoft.playready',
};

/**
 * @class Drm
 */
class Drm {
  /**
   * Build the keySystems object according to the DRM vendor.
   *
   * @param {Array.<import('../dataProvider/model/typedef').DrmMetadata>} drmList The DRM list from the media composition.
   *
   * @returns {import('./typedef').KeySystems} The resulting keySystems.
   */
  static buildKeySystems(drmList = []) {
    const keySystems = {};

    drmList.forEach((drmVendor) => {
      const type = Drm.vendors[drmVendor.type];

      if (Drm.vendors.FAIRPLAY === type) {
        keySystems[type] = Drm.buildFairplayConfig(drmVendor);
      } else {
        keySystems[type] = drmVendor.licenseUrl;
      }
    });

    return {
      keySystems,
    };
  }

  /**
   * Builds the configuration for Apple FairPlay.
   *
   * @param {import('../dataProvider/model/typedef').DrmMetadata} drmVendor the DRM metadata for FairPlay
   *
   * @returns {import('./typedef').KeySystemConfiguration} the FairPlay configuration
   */
  static buildFairplayConfig(drmVendor) {
    const { certificateUrl: certificateUri } = drmVendor;

    return {
      certificateUri,
      getContentId: function (emeOptions, contentId) {
        return contentId.replace('skd:', 'https:');
      },
      getLicense: function (emeOptions, licenseUri, keyMessage, callback) {
        Drm.requestLicense(licenseUri, keyMessage, callback);
      }
    };
  }

  /**
   * Fetch a DRM license from a license server.
   *
   * _If required this logic can also be used for widevine/playready_
   *
   * @param {string} url the license server URL
   * @param {ArrayBuffer} keyMessage the challenge generated by the browser's CDM
   * @param {(function(Error|null, ArrayBuffer):void)} callback the callback provided by videojs-contrib-eme receives (err, key)
   * @see https://github.com/videojs/videojs-contrib-eme/tree/main?tab=readme-ov-file#get-certificatecontent-idlicense-by-functions
   */
  static async requestLicense(url, keyMessage, callback) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: keyMessage,
        headers: {
          'Content-Type': 'application/octet-stream',
        }
      });

      if (!response.ok) {
        return callback(await Drm.parseLicenseError(response));
      }

      callback(null, await response.arrayBuffer());
    } catch (error) {
      callback(error);
    }
  }

  /**
   * Parses the error response from the license server.
   *
   * @param {Response} response the fetch response object
   *
   * @returns {Promise<string>} the formatted error message
   */
  static async parseLicenseError(response) {
    const errMessage = 'DRM license error:';

    try {
      const { code, message } = await response.json();

      return `${errMessage} ${code || response.status} ${message || response.statusText}`;
    } catch {
      return `${errMessage} ${response.status} ${response.statusText}`;
    }
  }

  /**
   * Check if some of the resources have DRM.
   *
   * @param {Array.<import('../dataProvider/model/typedef').MainResource>} resources
   *
   * @returns {boolean} true if some of the resources have DRM, false otherwise.
   */
  static hasDrm(resources) {
    return resources.some(({ drmList }) => drmList && drmList.length > 0);
  }

  /**
   * Get DRM vendors.
   */
  static get vendors() {
    return DRM_VENDORS;
  }
}

export default Drm;
